// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Typography } from '@altermaind/web/ui';
import { GoToPublicsDocs, Tooltip } from '@/_shared/components';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Charts } from '@/_shared/components/Charts';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { SurveyTemplate, useSurvey } from '@/surveys';
import { ChipsTemplate, SendSurvey } from '@/surveys/components';
import { getTemplatesCall, postEmployerByIdSurveyResultCall } from '@/surveys/state/api';
import { EnrollmentStatus } from '@/swagger/constants/surveys-enums';
import { Employer } from '../../../state';
import { EmptyState } from './EmptyState';

type Props = {
  employer: Employer;
};

export const TabAssessment: FC<Props> = ({ employer }) => {
  const t = useTranslations('people.details.assessment');
  const tShared = useTranslations();
  const [, setTrigger] = useState(false);

  const {
    isOpen: isOpenSendSurvey,
    closeDialog: closeDialogSendSurvey,
    openDialog: openDialogSendSurvey,
  } = useDialog('sendSurvey');

  const { genericResponsiveValue } = useResponsiveStyle();
  const [activeChip, setActiveChip] = useState<string>('');

  const { organizationHasCampaignsHardSkill, organizationHasCampaignsSoftSkill } = useSurvey();

  const {
    execute: executePostEmployerByIdSurveyResult,
    loading: loadingPostEmployerByIdSurveyResult,
    success: successPostEmployerByIdSurveyResult,
    error: errorPostEmployerByIdSurveyResult,
    response: responsePostEmployerByIdSurveyResult,
  } = postEmployerByIdSurveyResultCall.hook();

  const { response: responseGetTemplates } = getTemplatesCall.hook();

  const getDescriptionMetric = useCallback(
    (surveyTemplateId: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.id === surveyTemplateId)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  useEffect(() => {
    if (employer.email) {
      executePostEmployerByIdSurveyResult({
        username: employer.email,
      });
    }
  }, [employer.email, executePostEmployerByIdSurveyResult]);

  useEffect(() => {
    if (responsePostEmployerByIdSurveyResult && responsePostEmployerByIdSurveyResult.length > 0) {
      if (!organizationHasCampaignsHardSkill || !organizationHasCampaignsSoftSkill) {
        setActiveChip(responsePostEmployerByIdSurveyResult[0].templateName ?? '');
      }
    }
  }, [
    organizationHasCampaignsHardSkill,
    organizationHasCampaignsSoftSkill,
    responsePostEmployerByIdSurveyResult,
  ]);

  if (loadingPostEmployerByIdSurveyResult) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorPostEmployerByIdSurveyResult) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (employer.isComplete === false) {
    return (
      <Box mt='s' textAlign='center'>
        <Typography variant='h1' mb='xs'>
          {t('userNotValid.title')}
        </Typography>
        <Typography variant='p3/light'>{t('userNotValid.description')}</Typography>
      </Box>
    );
  }

  if (
    successPostEmployerByIdSurveyResult &&
    !loadingPostEmployerByIdSurveyResult &&
    !errorPostEmployerByIdSurveyResult &&
    employer
  ) {
    return (
      <Box>
        {responsePostEmployerByIdSurveyResult &&
          responsePostEmployerByIdSurveyResult?.length > 0 && (
            <Box mb='s'>
              <Typography variant='h1' mt='m' mb='xs'>
                {employer.firstName} {employer.lastName}
              </Typography>
            </Box>
          )}
        {organizationHasCampaignsHardSkill && organizationHasCampaignsSoftSkill && (
          <ChipsTemplate setActiveChip={setActiveChip} activeChip={activeChip} />
        )}
        {responsePostEmployerByIdSurveyResult?.length === 0 && <EmptyState name='' />}
        {responsePostEmployerByIdSurveyResult
          ?.filter(survey => survey.templateName === activeChip)
          .map(survey => {
            return (
              <>
                <Typography key={survey.templateId} variant='h3' mb='m'>
                  {t(!survey.completed ? 'surveyTitle.notCompleted' : 'surveyTitle.completed', {
                    name: survey.templateName,
                  })}
                </Typography>
                {survey.enrollmentStatus === EnrollmentStatus.NOT_INVITED && (
                  <>
                    <Card mb='m' p='l'>
                      <Box display='flex' justifyContent='space-between' alignContent='center'>
                        <Box
                          alignItems={genericResponsiveValue({
                            breakPoints: { desktop: 'center' },
                            defaultValue: 'flex-start',
                          })}
                          display='flex'
                          flexDirection={genericResponsiveValue({
                            breakPoints: { desktop: 'row' },
                            defaultValue: 'column',
                          })}
                        >
                          <Typography variant='h2'>
                            {t('title', {
                              name: survey.templateName,
                            })}
                          </Typography>
                          <Box
                            display='flex'
                            flexDirection={genericResponsiveValue({
                              breakPoints: { desktop: 'row' },
                              defaultValue: 'row',
                            })}
                            mt={genericResponsiveValue({
                              breakPoints: { desktop: 'none' },
                              defaultValue: 'xs',
                            })}
                            alignItems='center'
                          >
                            <Box
                              ml={genericResponsiveValue({
                                breakPoints: { desktop: 'xs' },
                                defaultValue: 'none',
                              })}
                              width='s'
                              height='s'
                              borderRadius='l'
                              backgroundColor='error'
                            />
                            <Typography ml='xxxs' variant='p2/light'>
                              {t('status.noSurvey')}
                            </Typography>
                            <Tooltip iconProps={{ size: 'xs' }} containerProps={{ ml: 'xxs' }}>
                              {t('status.tooltip.noSurvey')}
                            </Tooltip>
                          </Box>
                        </Box>
                      </Box>
                      <Box mt='s' display='flex' flexDirection='row' alignItems='center'>
                        <Box flex='1'>
                          <Typography mb='m' variant='p3/light'>
                            {t('descriptionNotSent')}
                          </Typography>
                          <Button
                            variant='positive'
                            onClick={openDialogSendSurvey}
                            disabled={!survey.templateId}
                          >
                            {t('sendSurvey')}
                          </Button>
                        </Box>
                      </Box>
                    </Card>
                    <SendSurvey
                      setTrigger={setTrigger}
                      preSelection
                      isOpen={isOpenSendSurvey}
                      closeDialog={closeDialogSendSurvey}
                    />
                  </>
                )}
                {survey.enrollmentStatus !== EnrollmentStatus.NOT_INVITED && (
                  <Card key={`${survey.templateId}${survey.templateVersion}`} mb='m' p='l'>
                    <Box display='flex' justifyContent='space-between' alignContent='center'>
                      <Box
                        alignItems={genericResponsiveValue({
                          breakPoints: { desktop: 'center' },
                          defaultValue: 'flex-start',
                        })}
                        display='flex'
                        flexDirection={genericResponsiveValue({
                          breakPoints: { desktop: 'row' },
                          defaultValue: 'column',
                        })}
                      >
                        <Typography variant='h2'>{survey.templateName}</Typography>
                        <Box
                          display='flex'
                          flexDirection={genericResponsiveValue({
                            breakPoints: { desktop: 'row' },
                            defaultValue: 'row',
                          })}
                          mt={genericResponsiveValue({
                            breakPoints: { desktop: 'none' },
                            defaultValue: 'xs',
                          })}
                          alignItems='center'
                        >
                          <Box
                            ml={genericResponsiveValue({
                              breakPoints: { desktop: 'xs' },
                              defaultValue: 'none',
                            })}
                            width='s'
                            height='s'
                            borderRadius='l'
                            backgroundColor={survey.completed ? 'success' : 'warning'}
                          />
                          <Typography ml='xxxs' variant='p2/light'>
                            {survey.completed
                              ? t('status.completed', {
                                  date: dayjs(survey.results?.completedAt).format('DD/MM/YYYY'),
                                })
                              : t('status.notCompleted')}
                          </Typography>
                          <Tooltip iconProps={{ size: 'xs' }} containerProps={{ ml: 'xxs' }}>
                            {t(`status.tooltip.${survey.completed ? 'completed' : 'notCompleted'}`)}
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      mt='s'
                      display='flex'
                      flexDirection={genericResponsiveValue({
                        breakPoints: { desktop: 'row' },
                        defaultValue: 'column',
                      })}
                      alignItems='flex-start'
                      mb='s'
                    >
                      {survey.completed && (
                        <>
                          <Box flex='1'>
                            <Typography
                              mb={genericResponsiveValue({
                                breakPoints: { desktop: 'm' },
                                defaultValue: 'none',
                              })}
                              variant='p3/light'
                            >
                              {t('descriptionCompleted', {
                                metricsCount: survey.results?.results?.length ?? 0,
                                templateName: survey.templateName,
                              })}
                            </Typography>
                            <Charts.RadarChartComponent
                              data={(survey.results?.results ?? []).map(metric => {
                                return {
                                  subject: metric.metric?.name ?? '',
                                  current: metric.score ?? 0,
                                  expected: 7,
                                  description: getDescriptionMetric(
                                    survey.templateId || '',
                                    metric.metric?.name || '',
                                  ),
                                };
                              })}
                            />
                          </Box>
                          <Box
                            flex='1'
                            pl={genericResponsiveValue({
                              breakPoints: { desktop: 'xs' },
                              defaultValue: 'none',
                            })}
                          >
                            <Typography mb='m' variant='h4'>
                              {t('descriptionTitle')}
                            </Typography>
                            <Typography mb='xs' variant='p2/light'>
                              {Object.keys(survey.results?.insights ?? {}).map(key => (
                                <Typography variant='p2/label' key={key}>
                                  {key} -{' '}
                                  <Typography as='span' variant='p2/light'>
                                    {(survey.results?.insights as Record<string, string>)[key]}
                                  </Typography>
                                  {'\n'}
                                </Typography>
                              ))}
                            </Typography>
                            {survey.templateName === SurveyTemplate.SoftSkillAssessment && (
                              <GoToPublicsDocs
                                label={t('discoverModel')}
                                suffixIcon='externalLink'
                                iconSize='xs'
                                goTo='CORE_CONCEPTS_AND_SLASH_MODEL'
                              />
                            )}
                          </Box>
                        </>
                      )}
                      {!survey.completed && (
                        <Box flex='1'>
                          <Typography variant='p3/light'>
                            {t('descriptionNotCompleted', {
                              templateName: survey.templateName,
                            })}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {survey.completed &&
                      survey.templateName === SurveyTemplate.SoftSkillAssessment && (
                        <AlertBadge iconName='lightBulb' type='primary' label={t('alertBadge')} />
                      )}
                  </Card>
                )}
              </>
            );
          })}
      </Box>
    );
  }

  return null;
};
